/* BYOB Builder - custom Build Your Own Gift Basket (Funderce)
   Vanilla JS. Integrates with the Flow theme via window.eventBus
   ('update:cart:drawer', 'open:cart:drawer') and routes.cart_add_url. */

(function () {
  'use strict';

  // Bump the version when the persisted shape/semantics change:
  // old drafts are dropped instead of restored wrongly
  var STORAGE_KEY = 'byob-box-v2';

  /* The order note's 'Gift box #N card: ...' lines are DERIVED state:
     recomputed from the card lines actually in the cart (their message/From
     properties), never appended or pattern-deleted. Any note text the
     shopper typed themselves is preserved. A guarded twin of this helper
     lives in the theme's cart-drawer BYOB block so cart surfaces work on
     pages where the builder script isn't loaded - keep both in sync. */
  if (!window.byobRebuildNote) {
    window.byobRebuildNote = function (cart, propNames) {
      var basketProp = (propNames && propNames.basket) || 'Box';
      var msgProp = (propNames && propNames.msg) || 'Gift message';
      var seen = {};
      var entries = [];
      (cart.items || []).forEach(function (item) {
        var p = item.properties || {};
        var label = p[basketProp];
        if (typeof label !== 'string' || label === '' || seen[label]) return;
        var msg = typeof p[msgProp] === 'string' ? p[msgProp] : '';
        var from = typeof p['From'] === 'string' ? p['From'] : '';
        if (msg === '' && from === '') return; // not the card line of this box
        seen[label] = true;
        var parts = [];
        // One note line per box: the full multiline text lives on the card
        // line's properties; the note carries a single-line summary
        if (msg !== '') parts.push('"' + msg.replace(/\s*\n+\s*/g, ' / ') + '"');
        if (from !== '') parts.push('From - ' + from);
        var num = label.match(/#(\d+)/);
        entries.push({
          sort: num ? parseInt(num[1], 10) : 1000000,
          line: 'Gift box ' + label + ' card: ' + parts.join(' | ')
        });
      });
      entries.sort(function (a, b) { return a.sort - b.sort; });
      // Drop existing card lines. Legacy entries (written before this fix)
      // can span lines when the message had newlines - consume those too by
      // following the quoted message until it closes.
      var kept = [];
      var lines = (cart.note || '').split('\n');
      var i = 0;
      while (i < lines.length) {
        var l = lines[i];
        var m = l.match(/^Gift box \S* ?card: ([\s\S]*)$/);
        if (m) {
          var quotes = (m[1].match(/"/g) || []).length;
          i++;
          while (quotes % 2 === 1 && i < lines.length) {
            quotes += (lines[i].match(/"/g) || []).length;
            i++;
          }
          continue;
        }
        kept.push(l);
        i++;
      }
      return kept.concat(entries.map(function (e) { return e.line; }))
        .join('\n').replace(/\n{3,}/g, '\n\n').trim();
    };
  }

  function initAll() {
    document.querySelectorAll('[data-byob-builder]').forEach(function (root) {
      if (root.dataset.byobInit === 'true') return;
      var configEl = root.querySelector('[data-byob-config]');
      if (!configEl) return; // section not configured yet
      root.dataset.byobInit = 'true';
      new ByobBuilder(root, JSON.parse(configEl.textContent));
    });
  }

  function ByobBuilder(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.items = new Map(); // variantId -> line
    this.card = {
      enabled: !!(cfg.card && cfg.cardRequired),
      variantId: cfg.card ? cfg.card.variantId : null,
      message: '',
      from: ''
    };
    this.filters = { search: '', cats: new Set(), colors: new Set() };
    this.fmt = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: cfg.currency || 'USD'
    });

    this.boxesInCart = 0;
    this.qvCard = null;
    this.productCache = {};
    this.cacheDom();
    // With a required card the design select starts on a placeholder;
    // mirror whatever the select actually shows (null until chosen)
    if (this.cardDesign) this.card.variantId = this.cardDesign.value || null;
    this.refreshBoxCount();
    this.bindToolbar();
    this.bindCards();
    this.bindQuickview();
    this.bindBarAndSheet();
    this.bindDrawerEvents();
    this.bindStickyOffset();
    this.liftChatLauncher();
    this.restore();
    this.updateFilters();
    this.updateSummary();
    this.track('byob_view', { items_available: this.cards.length });
  }

  ByobBuilder.prototype.cacheDom = function () {
    var r = this.root;
    this.cards = Array.prototype.slice.call(r.querySelectorAll('[data-byob-card]'));
    this.toolbar = r.querySelector('[data-byob-toolbar]');
    this.searchInput = r.querySelector('[data-byob-search]');
    this.searchClear = r.querySelector('[data-byob-search-clear]');
    this.catChips = Array.prototype.slice.call(r.querySelectorAll('[data-byob-cat]'));
    this.colorChips = Array.prototype.slice.call(r.querySelectorAll('[data-byob-color]'));
    this.countEl = r.querySelector('[data-byob-count]');
    this.emptyEl = r.querySelector('[data-byob-empty]');
    this.clearFiltersBtns = Array.prototype.slice.call(r.querySelectorAll('[data-byob-clear-filters]'));
    this.toolbarClear = r.querySelector('[data-byob-toolbar-clear]');
    this.qv = r.querySelector('[data-byob-qv]');
    this.qvImg = r.querySelector('[data-byob-qv-img]');
    this.qvThumbs = r.querySelector('[data-byob-qv-thumbs]');
    this.qvTitle = r.querySelector('[data-byob-qv-title]');
    this.qvPrice = r.querySelector('[data-byob-qv-price]');
    this.qvVariants = r.querySelector('[data-byob-qv-variants]');
    this.qvSelect = r.querySelector('[data-byob-qv-select]');
    this.qvAdd = r.querySelector('[data-byob-qv-add]');
    this.qvAddLabel = r.querySelector('[data-byob-qv-add-label]');
    this.qvStepper = r.querySelector('[data-byob-qv-stepper]');
    this.qvQty = r.querySelector('[data-byob-qv-qty]');
    this.qvNote = r.querySelector('[data-byob-qv-note]');
    this.qvDesc = r.querySelector('[data-byob-qv-desc]');
    this.steps = r.querySelectorAll('[data-byob-step]');
    this.bar = r.querySelector('[data-byob-bar]');
    this.barLabelText = r.querySelector('[data-byob-bar-label-text]');
    this.barIncart = r.querySelector('[data-byob-bar-incart]');
    this.barBadge = r.querySelector('[data-byob-bar-badge]');
    this.barItems = r.querySelector('[data-byob-bar-items]');
    this.barTotal = r.querySelector('[data-byob-bar-total]');
    this.barCta = r.querySelector('[data-byob-bar-cta]');
    this.sheet = r.querySelector('[data-byob-sheet]');
    this.sheetLines = r.querySelector('[data-byob-lines]');
    this.sheetEmpty = r.querySelector('[data-byob-sheet-empty]');
    this.sheetEmptyDefault = this.sheetEmpty ? this.sheetEmpty.textContent : '';
    this.statusEl = r.querySelector('[data-byob-status]');
    this.totalEl = r.querySelector('[data-byob-total]');
    this.ctaBtn = r.querySelector('[data-byob-cta]');
    this.ctaLabel = r.querySelector('[data-byob-cta-label]');
    this.spinner = r.querySelector('[data-byob-spinner]');
    this.errorEl = r.querySelector('[data-byob-error]');
    this.successPanel = r.querySelector('[data-byob-success]');
    this.successNum = r.querySelector('[data-byob-success-num]');
    this.cardToggle = r.querySelector('[data-byob-card-toggle]');
    this.cardFields = r.querySelector('[data-byob-card-fields]');
    this.cardDesign = r.querySelector('[data-byob-card-design]');
    this.cardMessage = r.querySelector('[data-byob-card-message]');
    this.cardFrom = r.querySelector('[data-byob-card-from]');
    this.msgCount = r.querySelector('[data-byob-msg-count]');
  };

  /* ---------- money ---------- */

  ByobBuilder.prototype.money = function (cents) {
    return this.fmt.format(cents / 100);
  };

  /* ---------- filters ---------- */

  ByobBuilder.prototype.bindToolbar = function () {
    var self = this;
    var debounceTimer;

    if (this.searchInput) {
      this.searchInput.addEventListener('input', function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () {
          self.filters.search = self.searchInput.value.trim().toLowerCase();
          if (self.searchClear) self.searchClear.hidden = self.filters.search === '';
          self.updateFilters();
          if (self.filters.search) self.track('byob_filter', { type: 'search', value: self.filters.search });
        }, 120);
      });
    }

    if (this.searchClear) {
      this.searchClear.addEventListener('click', function () {
        self.searchInput.value = '';
        self.filters.search = '';
        self.searchClear.hidden = true;
        self.updateFilters();
        self.searchInput.focus();
      });
    }

    this.catChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var cat = chip.dataset.byobCat;
        if (cat === '') {
          self.filters.cats.clear();
        } else if (self.filters.cats.has(cat)) {
          self.filters.cats.delete(cat);
        } else {
          self.filters.cats.add(cat);
          self.track('byob_filter', { type: 'category', value: cat });
        }
        self.updateFilters();
      });
    });

    this.colorChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var color = chip.dataset.byobColor;
        if (self.filters.colors.has(color)) {
          self.filters.colors.delete(color);
        } else {
          self.filters.colors.add(color);
          self.track('byob_filter', { type: 'color', value: color });
        }
        self.updateFilters();
      });
    });

    this.clearFiltersBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.filters.search = '';
        self.filters.cats.clear();
        self.filters.colors.clear();
        if (self.searchInput) {
          self.searchInput.value = '';
          if (self.searchClear) self.searchClear.hidden = true;
        }
        self.updateFilters();
      });
    });

    // Hide category chips with no products; show counts
    var counts = {};
    this.cards.forEach(function (card) {
      var cat = card.dataset.category || '';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    this.catChips.forEach(function (chip) {
      var cat = chip.dataset.byobCat;
      if (cat === '') return;
      var n = counts[cat] || 0;
      if (n === 0) {
        chip.hidden = true;
      } else {
        var countEl = chip.querySelector('[data-byob-cat-count]');
        if (countEl) countEl.textContent = ' ' + n;
      }
    });
  };

  ByobBuilder.prototype.matches = function (card) {
    var f = this.filters;
    if (f.search) {
      var hay = (card.dataset.title || '') + ' ' + (card.dataset.category || '').toLowerCase() + ' ' + (card.dataset.colors || '');
      if (hay.indexOf(f.search) === -1) return false;
    }
    if (f.cats.size > 0 && !f.cats.has(card.dataset.category || '')) return false;
    if (f.colors.size > 0) {
      var cardColors = (card.dataset.colors || '').split('|');
      var hit = false;
      f.colors.forEach(function (c) {
        if (cardColors.indexOf(c) !== -1) hit = true;
      });
      if (!hit) return false;
    }
    return true;
  };

  ByobBuilder.prototype.updateFilters = function () {
    var self = this;
    var visible = 0;

    this.cards.forEach(function (card) {
      var show = self.matches(card);
      card.classList.toggle('byob-card--hidden', !show);
      if (show) visible += 1;
    });

    this.catChips.forEach(function (chip) {
      var cat = chip.dataset.byobCat;
      var active = cat === '' ? self.filters.cats.size === 0 : self.filters.cats.has(cat);
      chip.classList.toggle('byob-chip--active', active);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.colorChips.forEach(function (chip) {
      var active = self.filters.colors.has(chip.dataset.byobColor);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (this.countEl) {
      this.countEl.textContent = 'Showing ' + visible + ' of ' + this.cards.length + ' gifts';
    }
    if (this.toolbarClear) {
      var anyFilter = !!(this.filters.search || this.filters.cats.size > 0 || this.filters.colors.size > 0);
      this.toolbarClear.hidden = !anyFilter;
    }
    if (this.emptyEl) this.emptyEl.hidden = visible !== 0;
    this.keepToolbarVisible();
  };

  ByobBuilder.prototype.keepToolbarVisible = function () {
    // Filtering while scrolled deep can collapse the grid so much that the
    // page shrinks under the scroll position, unsticking the toolbar and
    // leaving the shopper at the footer - pull the toolbar back into view
    if (!this.toolbar) return;
    var offset = parseFloat(getComputedStyle(this.root).getPropertyValue('--byob-sticky-top')) || 0;
    var top = this.toolbar.getBoundingClientRect().top;
    if (top < offset - 2) {
      window.scrollTo(0, window.scrollY + top - offset);
    }
  };

  /* ---------- selection ---------- */

  ByobBuilder.prototype.bindCards = function () {
    var self = this;

    this.cards.forEach(function (card) {
      var addBtn = card.querySelector('[data-byob-add]');
      var stepper = card.querySelector('[data-byob-stepper]');
      var minus = card.querySelector('[data-byob-minus]');
      var plus = card.querySelector('[data-byob-plus]');
      var variants = card.querySelector('[data-byob-variants]');
      var priceEl = card.querySelector('[data-byob-card-price]');

      if (variants) {
        variants.addEventListener('change', function () {
          var opt = variants.options[variants.selectedIndex];
          card.dataset.variant = opt.value;
          card.dataset.price = opt.dataset.price;
          if (priceEl) priceEl.textContent = self.money(parseInt(opt.dataset.price, 10));
          var img = card.querySelector('[data-byob-img]');
          if (img && opt.dataset.image) img.src = opt.dataset.image;
          // Each variant is its own line: the card now shows the state of
          // the newly selected variant (Add, or its own quantity stepper),
          // so "one of each" works with independent quantities per variant
          self.syncCard(card);
        });
      }

      if (addBtn && !addBtn.disabled) {
        addBtn.addEventListener('click', function () {
          self.addItem(card, 1);
        });
      }
      if (plus) {
        plus.addEventListener('click', function () {
          self.addItem(card, 1);
        });
      }
      if (minus) {
        minus.addEventListener('click', function () {
          self.addItem(card, -1);
        });
      }
    });
  };

  /* ---------- quick view ---------- */

  ByobBuilder.prototype.bindQuickview = function () {
    var self = this;
    if (!this.qv) return;

    // Card image/title clicks open the quick view instead of navigating;
    // the links keep their href as a no-JS fallback
    this.root.addEventListener('click', function (e) {
      var link = e.target.closest('.byob-card__media-link, .byob-card__title-link');
      if (!link) return;
      var card = link.closest('[data-byob-card]');
      if (!card) return;
      e.preventDefault();
      self.openQuickview(card);
    });

    var closer = this.qv.querySelector('[data-byob-qv-close]');
    if (closer) closer.addEventListener('click', function () { self.qv.close(); });
    // A click that lands on the dialog element itself (not its content)
    // can only come from the backdrop - close
    this.qv.addEventListener('click', function (e) {
      if (e.target === self.qv) self.qv.close();
    });
    this.qv.addEventListener('close', function () { self.qvCard = null; });

    if (this.qvAdd) {
      this.qvAdd.addEventListener('click', function () {
        if (self.qvCard) { self.addItem(self.qvCard, 1); self.syncQuickview(); }
      });
    }
    var plus = this.qv.querySelector('[data-byob-qv-plus]');
    var minus = this.qv.querySelector('[data-byob-qv-minus]');
    if (plus) {
      plus.addEventListener('click', function () {
        if (self.qvCard) { self.addItem(self.qvCard, 1); self.syncQuickview(); }
      });
    }
    if (minus) {
      minus.addEventListener('click', function () {
        if (self.qvCard) { self.addItem(self.qvCard, -1); self.syncQuickview(); }
      });
    }

    if (this.qvSelect) {
      this.qvSelect.addEventListener('change', function () {
        var card = self.qvCard;
        if (!card) return;
        // The grid card is the source of truth: push the choice down to its
        // select so price/image/dataset and per-variant lines stay one system
        var cardSelect = card.querySelector('[data-byob-variants]');
        if (cardSelect) {
          cardSelect.value = self.qvSelect.value;
          cardSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
        var opt = self.qvSelect.options[self.qvSelect.selectedIndex];
        if (opt && opt.dataset.image) self.qvImg.src = opt.dataset.image;
        self.syncQuickview();
      });
    }

    if (this.qvThumbs) {
      this.qvThumbs.addEventListener('click', function (e) {
        var t = e.target.closest('[data-byob-qv-thumb]');
        if (!t) return;
        self.qvImg.src = t.dataset.byobQvThumb;
        Array.prototype.forEach.call(self.qvThumbs.children, function (c) {
          c.classList.toggle('byob-qv__thumb--active', c === t);
        });
      });
    }
  };

  ByobBuilder.prototype.openQuickview = function (card) {
    var self = this;
    this.qvCard = card;

    // Paint instantly from what the card already knows, then enrich from
    // the product JSON (description, gallery, variant availability)
    var img = card.querySelector('[data-byob-img]');
    var title = card.querySelector('.byob-card__title').textContent.trim();
    this.qvImg.src = img ? (img.currentSrc || img.src) : '';
    this.qvImg.alt = title;
    this.qvTitle.textContent = title;
    this.qvDesc.innerHTML = '';
    this.qvThumbs.hidden = true;
    this.qvThumbs.innerHTML = '';
    this.qvVariants.hidden = true;
    this.syncQuickview();
    if (!this.qv.open) this.qv.showModal();
    this.track('byob_quickview', { item_name: title });

    var handle = card.dataset.handle;
    if (this.productCache[handle]) {
      this.renderQuickviewProduct(this.productCache[handle]);
      return;
    }
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) return;
        self.productCache[handle] = p;
        if (self.qvCard === card) self.renderQuickviewProduct(p);
      })
      .catch(function () {});
  };

  ByobBuilder.prototype.qvImageUrl = function (src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  };

  ByobBuilder.prototype.renderQuickviewProduct = function (p) {
    var self = this;
    var card = this.qvCard;
    if (!card) return;

    this.qvDesc.innerHTML = p.description || '';

    var imgs = (p.images || []).slice(0, 6);
    if (imgs.length > 1) {
      this.qvThumbs.innerHTML = '';
      imgs.forEach(function (src, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'byob-qv__thumb' + (i === 0 ? ' byob-qv__thumb--active' : '');
        b.dataset.byobQvThumb = self.qvImageUrl(src, 600);
        b.setAttribute('data-byob-qv-thumb', b.dataset.byobQvThumb);
        var im = document.createElement('img');
        im.src = self.qvImageUrl(src, 120);
        im.alt = '';
        im.loading = 'lazy';
        b.appendChild(im);
        self.qvThumbs.appendChild(b);
      });
      this.qvThumbs.hidden = false;
    }
    if (imgs.length > 0) this.qvImg.src = this.qvImageUrl(imgs[0], 600);

    var variants = p.variants || [];
    if (variants.length > 1 && this.qvSelect) {
      this.qvSelect.innerHTML = '';
      variants.forEach(function (v) {
        var o = document.createElement('option');
        o.value = String(v.id);
        o.textContent = v.title + (v.available ? '' : ' - sold out');
        o.disabled = !v.available;
        if (v.featured_image && v.featured_image.src) {
          o.dataset.image = self.qvImageUrl(v.featured_image.src, 600);
        }
        self.qvSelect.appendChild(o);
      });
      this.qvSelect.value = card.dataset.variant;
      this.qvVariants.hidden = false;
    }
  };

  ByobBuilder.prototype.syncQuickview = function () {
    var card = this.qvCard;
    if (!card || !this.qv) return;
    var available = card.dataset.available === 'true';
    var line = this.items.get(card.dataset.variant);
    if (this.qvPrice) this.qvPrice.textContent = this.money(parseInt(card.dataset.price, 10));
    if (!available) {
      this.qvAdd.hidden = false;
      this.qvAdd.disabled = true;
      this.qvAddLabel.textContent = 'Sold out';
      this.qvStepper.hidden = true;
      this.qvNote.hidden = true;
      return;
    }
    this.qvAdd.disabled = false;
    if (line) {
      this.qvAdd.hidden = true;
      this.qvStepper.hidden = false;
      this.qvQty.textContent = String(line.qty);
      this.qvNote.hidden = false;
    } else {
      this.qvAdd.hidden = false;
      this.qvAddLabel.textContent = 'Add to gift box';
      this.qvStepper.hidden = true;
      this.qvNote.hidden = true;
    }
  };

  ByobBuilder.prototype.giftCount = function () {
    var n = 0;
    this.items.forEach(function (line) { n += line.qty; });
    return n;
  };

  ByobBuilder.prototype.addItem = function (card, delta) {
    var variantId = card.dataset.variant;
    var line = this.items.get(variantId);
    var max = this.cfg.maxItems;

    if (delta > 0) {
      this.justAdded = null; // building a new box: drop the "box added" notice
      if (max > 0 && this.giftCount() >= max) {
        this.setStatus('You can add up to ' + max + ' gifts per box.');
        return;
      }
      if (line) {
        if (!this.cfg.allowQty) {
          // Quantities disabled: a second tap toggles the gift back off
          this.items.delete(variantId);
          this.track('byob_remove_item', { item_name: line.title });
          this.syncCard(card);
          this.updateSummary();
          return;
        }
        if (line.qty >= 10) return;
        line.qty += 1;
      } else {
        var img = card.querySelector('[data-byob-img]');
        var variantSelect = card.querySelector('[data-byob-variants]');
        line = {
          productId: card.dataset.id,
          variantId: variantId,
          qty: 1,
          title: card.querySelector('.byob-card__title').textContent,
          variantTitle: variantSelect ? variantSelect.options[variantSelect.selectedIndex].text : '',
          price: parseInt(card.dataset.price, 10),
          img: img ? img.currentSrc || img.src : ''
        };
        this.items.set(variantId, line);
        this.track('byob_add_item', { item_name: line.title, price: line.price / 100 });
      }
    } else if (line) {
      line.qty -= 1;
      if (line.qty <= 0) {
        this.items.delete(variantId);
        this.track('byob_remove_item', { item_name: line.title });
      }
    }

    this.syncCard(card);
    this.updateSummary();
  };

  ByobBuilder.prototype.syncCard = function (card) {
    var line = this.items.get(card.dataset.variant);
    var addBtn = card.querySelector('[data-byob-add]');
    var stepper = card.querySelector('[data-byob-stepper]');
    var qtyEl = card.querySelector('[data-byob-qty]');
    var label = card.querySelector('[data-byob-add-label]');

    // The card highlights if ANY variant of this product is in the box;
    // the Add/stepper below reflects only the currently selected variant
    var totalQty = 0;
    this.items.forEach(function (l) {
      if (l.productId === card.dataset.id) totalQty += l.qty;
    });
    card.classList.toggle('byob-card--selected', totalQty > 0);
    var inbox = card.querySelector('[data-byob-inbox]');
    if (inbox) {
      inbox.hidden = totalQty === 0;
      if (totalQty > 0) inbox.textContent = totalQty + ' in box';
    }

    if (this.cfg.allowQty && stepper) {
      if (addBtn) addBtn.hidden = !!line;
      stepper.hidden = !line;
      if (line && qtyEl) qtyEl.textContent = line.qty;
      if (addBtn) addBtn.setAttribute('aria-pressed', line ? 'true' : 'false');
    } else if (addBtn) {
      addBtn.setAttribute('aria-pressed', line ? 'true' : 'false');
      if (label) label.textContent = line ? 'Added' : 'Add';
    }
  };

  ByobBuilder.prototype.syncAllCards = function () {
    var self = this;
    this.cards.forEach(function (card) { self.syncCard(card); });
  };

  /* ---------- summary bar + sheet ---------- */

  ByobBuilder.prototype.bindBarAndSheet = function () {
    var self = this;
    var opener = this.root.querySelector('[data-byob-open-sheet]');
    var closer = this.root.querySelector('[data-byob-close-sheet]');

    if (opener) opener.addEventListener('click', function () { self.openSheet(); });
    if (this.barCta) {
      this.barCta.addEventListener('click', function () { self.openSheet(); });
    }
    if (closer) closer.addEventListener('click', function () { self.closeSheet(); });

    if (this.sheet) {
      // Close when tapping the backdrop
      this.sheet.addEventListener('click', function (e) {
        if (e.target === self.sheet) self.closeSheet();
      });
      this.sheet.addEventListener('close', function () {
        self.exitSuccess();
        if (self.lastFocus) self.lastFocus.focus();
      });
    }

    var buildAnother = this.root.querySelector('[data-byob-build-another]');
    if (buildAnother) {
      buildAnother.addEventListener('click', function () {
        // Step 2 -> step 1 in place: the dialog never closes itself between
        // steps. The shopper dismisses it when ready to browse the grid.
        self.exitSuccess();
        self.updateSummary();
      });
    }
    this.root.querySelectorAll('[data-byob-view-cart]').forEach(function (viewCart) {
      viewCart.addEventListener('click', function () {
        self.closeSheet();
        var pageContainer = document.getElementById('PageContainer');
        var cartType = pageContainer ? pageContainer.dataset.cartType : '';
        if (window.eventBus && cartType === 'drawer') {
          window.eventBus.emit('open:cart:drawer', { scrollToTop: true });
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      });
    });

    if (this.ctaBtn) {
      this.ctaBtn.addEventListener('click', function () { self.submit(); });
    }

    if (this.cardToggle) {
      this.cardToggle.addEventListener('change', function () {
        self.card.enabled = self.cardToggle.checked;
        if (self.cardFields) self.cardFields.hidden = !self.card.enabled;
        if (self.card.enabled) self.track('byob_card_added', {});
        self.updateSummary();
      });
    }
    if (this.cardDesign) {
      this.cardDesign.addEventListener('change', function () {
        self.card.variantId = self.cardDesign.value || null;
        self.updateSummary();
      });
    }
    if (this.cardMessage) {
      this.cardMessage.addEventListener('input', function () {
        self.card.message = self.cardMessage.value;
        if (self.msgCount) self.msgCount.textContent = String(self.cardMessage.value.length);
        self.persist();
      });
    }
    if (this.cardFrom) {
      this.cardFrom.addEventListener('input', function () {
        self.card.from = self.cardFrom.value;
        self.persist();
      });
    }
  };

  ByobBuilder.prototype.bindStickyOffset = function () {
    // The theme header is sticky; the toolbar must stick just below it,
    // not slide underneath. Measure the header and expose it as a CSS var.
    var self = this;
    var measure = function () {
      var top = 0;
      var candidates = document.querySelectorAll('.header-section, #shopify-section-header, .shopify-section-group-header-group');
      candidates.forEach(function (el) {
        var pos = getComputedStyle(el).position;
        if (pos === 'sticky' || pos === 'fixed') {
          top = Math.max(top, el.getBoundingClientRect().height);
        }
      });
      // Whole pixels only: fractional header heights create hairline gaps
      self.root.style.setProperty('--byob-sticky-top', Math.floor(top) + 'px');
    };
    measure();
    var timer;
    var lazyMeasure = function () {
      clearTimeout(timer);
      timer = setTimeout(measure, 200);
    };
    window.addEventListener('resize', lazyMeasure);
    // Sticky headers often change height (shrink) once scrolled
    window.addEventListener('scroll', lazyMeasure, { passive: true });

    // Category/colour rows: translate mouse wheel into horizontal scroll
    this.root.querySelectorAll('.byob-chips, .byob-colors').forEach(function (row) {
      row.addEventListener('wheel', function (e) {
        if (row.scrollWidth > row.clientWidth && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          row.scrollLeft += e.deltaY;
          e.preventDefault();
        }
      }, { passive: false });
    });
  };

  ByobBuilder.prototype.bindDrawerEvents = function () {
    // Slide the summary bar out of the way whenever a theme drawer
    // (cart drawer, menu, offers) is open. The theme's drawer:opened/closed
    // bus events do NOT fire reliably on real transitions - the drawers'
    // aria-hidden attribute does, so observe that instead.
    var self = this;
    var drawers = document.querySelectorAll('[data-drawer-position]');
    if (!drawers.length) return;
    var wasOpen = false;
    var update = function () {
      var anyOpen = false;
      drawers.forEach(function (d) {
        if (d.getAttribute('aria-hidden') === 'false') anyOpen = true;
      });
      self.root.classList.toggle('byob-builder--drawer-open', anyOpen);
      if (wasOpen && !anyOpen) {
        // Drawer just closed: cart may have changed (box removed etc.)
        self.refreshBoxCount();
      }
      wasOpen = anyOpen;
    };
    drawers.forEach(function (d) {
      new MutationObserver(update).observe(d, { attributes: true, attributeFilter: ['aria-hidden'] });
    });
    update();
  };

  ByobBuilder.prototype.liftChatLauncher = function () {
    // Tidio renders its launcher inside an open shadow root, which document
    // CSS can't reach (the stylesheet's #tidio-chat iframe rule only covers
    // the legacy iframe embed). Inject the lift into the root itself; the
    // widget mounts late, so poll for it. If Tidio renames its internals
    // this quietly no-ops and the launcher keeps its default spot.
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      var host = document.getElementById('tidio-chat');
      var root = host && host.shadowRoot;
      if (root) {
        clearInterval(timer);
        if (root.querySelector('#byob-chat-lift')) return;
        var st = document.createElement('style');
        st.id = 'byob-chat-lift';
        // #button is position:relative, so a bottom offset shifts it up
        // without touching the transform Tidio animates; top:auto because
        // Tidio sets top:0, which beats bottom on relative elements;
        // #chat-message is the proactive tooltip
        st.textContent = '@media (max-width: 989px){#button,#chat-message{top:auto !important;bottom:calc(80px + env(safe-area-inset-bottom,0px)) !important}}';
        root.appendChild(st);
      } else if (tries > 40) {
        clearInterval(timer);
      }
    }, 750);
  };

  ByobBuilder.prototype.refreshBoxCount = function () {
    // Count COMPLETE boxes in the cart (box product + card + min gifts),
    // mirroring the drawer's grouping rules
    var self = this;
    fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var groups = {};
        (cart.items || []).forEach(function (item) {
          var label = item.properties && item.properties[self.cfg.basketProp];
          if (!label) return;
          var g = groups[label] = groups[label] || { box: false, card: false, gifts: 0 };
          if (self.cfg.box && item.handle === self.cfg.box.handle) g.box = true;
          else if (self.cfg.card && item.handle === self.cfg.card.handle) g.card = true;
          else g.gifts += item.quantity;
        });
        var count = 0;
        for (var k in groups) {
          var g = groups[k];
          var cardOk = self.cfg.card ? g.card : true;
          if (g.box && cardOk && g.gifts >= self.cfg.minItems) count += 1;
        }
        self.boxesInCart = count;
        self.updateBoxCountUI();
      })
      .catch(function () { /* indicator only - never break the builder */ });
  };

  ByobBuilder.prototype.updateBoxCountUI = function () {
    var n = this.boxesInCart;
    var incart = this.root.querySelector('[data-byob-incart]');
    var incartText = this.root.querySelector('[data-byob-incart-text]');
    if (incart) {
      incart.hidden = n === 0;
      if (incartText) incartText.textContent = n === 1 ? '1 gift box is already in your cart.' : n + ' gift boxes are already in your cart.';
    }
    // Refresh the bar label suffix
    this.updateSummary();
  };

  ByobBuilder.prototype.openSheet = function () {
    if (!this.sheet || this.sheet.open) return;
    this.exitSuccess();
    this.lastFocus = document.activeElement;
    this.sheet.showModal();
    this.track('byob_view_basket', { gifts: this.giftCount() });
  };

  ByobBuilder.prototype.showSuccess = function () {
    if (!this.sheet || !this.successPanel) return;
    if (this.successNum) this.successNum.textContent = this.justAdded || '';
    this.sheet.classList.add('byob-sheet--success');
    this.successPanel.hidden = false;
    if (!this.sheet.open) this.sheet.showModal();
  };

  ByobBuilder.prototype.exitSuccess = function () {
    if (!this.sheet || !this.successPanel) return;
    this.sheet.classList.remove('byob-sheet--success');
    this.successPanel.hidden = true;
  };

  ByobBuilder.prototype.closeSheet = function () {
    if (this.sheet && this.sheet.open) this.sheet.close();
  };

  ByobBuilder.prototype.total = function () {
    var t = this.cfg.box.price;
    this.items.forEach(function (line) { t += line.price * line.qty; });
    if (this.card.enabled && this.cfg.card) t += this.cfg.card.price;
    return t;
  };

  ByobBuilder.prototype.needsCardDesign = function () {
    // Only when a required card offers multiple designs (select present)
    return !!(this.cfg.card && this.cfg.cardRequired && this.cardDesign && !this.card.variantId);
  };

  ByobBuilder.prototype.updateSummary = function () {
    var count = this.giftCount();
    var min = this.cfg.minItems;
    var missing = Math.max(0, min - count);
    var giftsReady = missing === 0;
    var ready = giftsReady && !this.needsCardDesign();
    var cardIncluded = !!(this.cfg.card && this.cfg.cardRequired);

    var showAddedNotice = count === 0 && !!this.justAdded;
    if (this.barBadge) {
      this.barBadge.textContent = String(count);
      // A "0" badge says nothing and eats the width the text needs on
      // small phones (clips the "$10.00" in "Box & card · $10.00");
      // show the badge only once there is something to count
      this.barBadge.hidden = count === 0;
    }
    if (this.barLabelText) {
      this.barLabelText.textContent = showAddedNotice ? 'Box ' + this.justAdded + ' added' : 'Your gift box';
    }
    if (this.barIncart) {
      var showIncart = !showAddedNotice && this.boxesInCart > 0;
      this.barIncart.hidden = !showIncart;
      if (showIncart) this.barIncart.textContent = ' · ' + this.boxesInCart + ' in cart';
    }
    if (this.barItems) {
      var base = cardIncluded ? 'Box & card' : 'Box only';
      // "Start another?" not "Start another box?": with the badge gone the
      // notice fits phones down to ~360px; the label above supplies "Box"
      if (showAddedNotice) base = 'Start another?';
      this.barItems.textContent = count === 0 ? base : 'Box + ' + count + (count === 1 ? ' gift' : ' gifts');
    }
    if (this.barTotal) this.barTotal.textContent = showAddedNotice ? '' : this.money(this.total());
    var sep = this.root.querySelector('[data-byob-bar-sep]');
    if (sep) sep.hidden = showAddedNotice;
    if (this.sheetEmpty) {
      this.sheetEmpty.textContent = showAddedNotice
        ? 'Box ' + this.justAdded + ' is in your cart. Start another box?'
        : this.sheetEmptyDefault;
    }

    if (this.barCta) {
      // Below the minimum the CTA is a status, not an action; the summary
      // side of the bar still opens the sheet for a peek
      this.barCta.disabled = !giftsReady;
      this.barCta.textContent = giftsReady ? 'Continue' : 'Add ' + missing + ' more';
    }

    // Steps
    var cardDone = this.cfg.cardRequired ? !this.needsCardDesign() : this.card.enabled;
    this.steps.forEach(function (step) {
      var n = step.dataset.byobStep;
      if (n === '2') step.classList.toggle('byob-steps__item--done', giftsReady);
      if (n === '3') step.classList.toggle('byob-steps__item--done', cardDone);
    });

    // Sheet
    this.renderLines();
    if (this.sheetEmpty) this.sheetEmpty.hidden = count !== 0;
    if (this.totalEl) this.totalEl.textContent = this.money(this.total());
    if (this.ctaBtn) this.ctaBtn.disabled = !ready;
    if (!giftsReady) {
      this.setStatus('Add ' + missing + ' more ' + (missing === 1 ? 'gift' : 'gifts') + ' to complete your gift box.');
    } else if (this.needsCardDesign()) {
      this.setStatus('Choose a design for your greeting card.');
    } else {
      this.setStatus('Your gift box is ready.');
    }

    this.persist();
  };

  ByobBuilder.prototype.renderLines = function () {
    if (!this.sheetLines) return;
    var self = this;
    this.sheetLines.textContent = '';

    this.items.forEach(function (line) {
      var li = document.createElement('li');
      li.className = 'byob-line';

      if (line.img) {
        var img = document.createElement('img');
        img.className = 'byob-line__img';
        img.src = line.img;
        img.alt = '';
        img.width = 48;
        img.height = 48;
        img.loading = 'lazy';
        li.appendChild(img);
      }

      var info = document.createElement('span');
      info.className = 'byob-line__info';
      var title = document.createElement('span');
      title.className = 'byob-line__title';
      title.textContent = line.title;
      info.appendChild(title);
      if (line.variantTitle && line.variantTitle.indexOf('Default') === -1) {
        var vt = document.createElement('span');
        vt.className = 'byob-line__variant';
        vt.textContent = line.variantTitle;
        info.appendChild(vt);
      }

      if (self.cfg.allowQty) {
        // Quantity sits under the title; minus at 1 removes the gift
        var qty = document.createElement('span');
        qty.className = 'byob-line__qty';
        var minus = document.createElement('button');
        minus.type = 'button';
        minus.className = 'byob-line__qty-btn';
        minus.textContent = '−';
        minus.setAttribute('aria-label', line.qty === 1 ? 'Remove ' + line.title : 'Remove one ' + line.title);
        minus.addEventListener('click', function () { self.changeLine(line.variantId, -1); });
        var num = document.createElement('span');
        num.className = 'byob-line__qty-num';
        num.textContent = String(line.qty);
        var plus = document.createElement('button');
        plus.type = 'button';
        plus.className = 'byob-line__qty-btn';
        plus.textContent = '+';
        plus.setAttribute('aria-label', 'Add one more ' + line.title);
        plus.addEventListener('click', function () { self.changeLine(line.variantId, 1); });
        qty.appendChild(minus);
        qty.appendChild(num);
        qty.appendChild(plus);
        info.appendChild(qty);
      }

      li.appendChild(info);

      var price = document.createElement('span');
      price.className = 'byob-line__price';
      price.textContent = self.money(line.price * line.qty);
      li.appendChild(price);

      self.sheetLines.appendChild(li);
    });
  };

  ByobBuilder.prototype.changeLine = function (variantId, delta) {
    var line = this.items.get(variantId);
    if (!line) return;
    if (delta > 0) {
      var max = this.cfg.maxItems;
      if (max > 0 && this.giftCount() >= max) return;
      if (line.qty >= 10) return;
      line.qty += 1;
    } else {
      line.qty += delta;
      if (line.qty <= 0) this.items.delete(variantId);
    }
    this.syncAllCards();
    this.updateSummary();
  };

  ByobBuilder.prototype.setStatus = function (text) {
    if (this.statusEl) this.statusEl.textContent = text;
  };

  /* ---------- cart submit ---------- */

  ByobBuilder.prototype.nextBasketLabel = function () {
    // Match the numbering shoppers and fulfillment already know from the
    // old app ("#1", "#2"): find the highest basket number in the cart.
    var self = this;
    var read = function () {
      return fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js', {
        headers: { Accept: 'application/json' }
      }).then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      });
    };
    var toLabel = function (cart) {
      var max = 0;
      (cart.items || []).forEach(function (item) {
        var value = item.properties && item.properties[self.cfg.basketProp];
        var m = typeof value === 'string' ? value.match(/#(\d+)/) : null;
        if (m) max = Math.max(max, parseInt(m[1], 10));
      });
      return '#' + (max + 1);
    };
    return read().then(toLabel).catch(function () {
      // Transient fetch failures (aggressive script optimizers, flaky
      // mobile networks) are the usual culprit - retry once before giving up
      return new Promise(function (resolve) { setTimeout(resolve, 400); })
        .then(read)
        .then(toLabel)
        .catch(function () {
          // Cart unreadable twice: fall back to a digits-only token so the
          // label still groups AND still reads like a number in packing
          // notes (the old base-36 fallback produced labels like "#BHRU").
          // Collision with real numbering would need an 800+ box cart.
          self.track('byob_label_fallback', {});
          return '#' + String(Date.now()).slice(-3);
        });
    });
  };

  ByobBuilder.prototype.submit = function () {
    var self = this;
    if (this.giftCount() < this.cfg.minItems || this.busy) return;
    if (this.needsCardDesign()) {
      this.openSheet();
      this.cardDesign.focus();
      return;
    }

    this.busy = true;
    if (this.spinner) this.spinner.hidden = false;
    this.ctaBtn.disabled = true;
    if (this.errorEl) this.errorEl.hidden = true;

    if (this.cfg.basketProp) {
      this.nextBasketLabel().then(function (label) {
        var props = {};
        props[self.cfg.basketProp] = label;
        self.addBasketToCart(props);
      });
    } else {
      this.addBasketToCart({});
    }
  };

  ByobBuilder.prototype.addBasketToCart = function (props) {
    var self = this;
    var basketLabel = this.cfg.basketProp ? props[this.cfg.basketProp] : '';
    var items = [];
    items.push({ id: this.cfg.box.variantId, quantity: 1, properties: props });
    this.items.forEach(function (line) {
      items.push({ id: parseInt(line.variantId, 10), quantity: line.qty, properties: props });
    });
    if (this.card.enabled && this.cfg.card) {
      var cardProps = {};
      for (var k in props) cardProps[k] = props[k];
      var msg = this.card.message.trim();
      var from = this.card.from.trim();
      if (msg !== '') cardProps[this.cfg.msgProp] = msg;
      if (from !== '') cardProps['From'] = from;
      // The card line's properties are the source of truth for the full
      // card text; the order-note copy is rebuilt from them after the add
      items.push({ id: parseInt(this.card.variantId, 10), quantity: 1, properties: cardProps });
    }

    var body = { items: items };
    var pageContainer = document.getElementById('PageContainer');
    var cartType = pageContainer ? pageContainer.dataset.cartType : '';
    if (cartType === 'drawer') body.sections = 'cart-drawer';

    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        return response.json().then(function (json) {
          if (!response.ok) throw json;
          return json;
        });
      })
      .then(function (json) {
        self.track('add_to_cart', {
          currency: self.cfg.currency,
          value: self.total() / 100,
          items: items.map(function (i) { return { item_id: i.id, quantity: i.quantity }; })
        });
        self.track('byob_basket_complete', { gifts: self.giftCount(), value: self.total() / 100 });

        // Remember which box just landed in the cart so the bar can invite
        // the shopper to start another one after the builder resets
        self.justAdded = basketLabel || '';
        self.reset();
        self.refreshBoxCount();

        var proceed = function (renderJson) {
          if (window.eventBus && cartType === 'drawer') {
            // Step 2 of the same sheet: confirm + offer the two next steps.
            // The drawer content refreshes silently so "View cart" is instant.
            window.eventBus.emit('update:cart:drawer', renderJson);
            self.updateCartCount();
            self.showSuccess();
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        };

        // Rebuild the note's card lines from the cart itself (best-effort -
        // the card line's own properties carry the text regardless). Runs
        // even when this box has no message: it also heals a stale line left
        // behind by a box removed outside our "Remove box" controls.
        var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var rebuilt = window.byobRebuildNote(cart, {
              basket: self.cfg.basketProp || 'Box',
              msg: self.cfg.msgProp || 'Gift message'
            });
            if (rebuilt === (cart.note || '')) return null;
            var body = { note: rebuilt };
            if (cartType === 'drawer') body.sections = 'cart-drawer';
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body)
            }).then(function (r) { return r.json(); });
          })
          .then(function (updJson) { proceed(updJson || json); })
          .catch(function () { proceed(json); });
      })
      .catch(function (err) {
        var msg = (err && (err.description || err.message)) || 'Something went wrong - please try again.';
        if (self.errorEl) {
          self.errorEl.textContent = msg;
          self.errorEl.hidden = false;
        }
      })
      .then(function () {
        self.busy = false;
        if (self.spinner) self.spinner.hidden = true;
        self.updateSummary();
      });
  };

  ByobBuilder.prototype.updateCartCount = function () {
    try {
      fetch(((window.routes && window.routes.cart_url) || '/cart') + '?view=compare')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (window.wetheme && typeof window.wetheme.updateCartCount === 'function') {
            window.wetheme.updateCartCount(cart);
          }
        });
    } catch (e) { /* non-critical */ }
  };

  ByobBuilder.prototype.reset = function () {
    this.items.clear();
    this.card.enabled = !!(this.cfg.card && this.cfg.cardRequired);
    this.card.message = '';
    if (this.cardToggle) this.cardToggle.checked = false;
    if (this.cardFields) this.cardFields.hidden = !this.card.enabled;
    if (this.cardMessage) this.cardMessage.value = '';
    if (this.msgCount) this.msgCount.textContent = '0';
    this.card.from = '';
    if (this.cardFrom) this.cardFrom.value = '';
    if (this.cardDesign) {
      // Required card: back to the "Choose your card design" placeholder
      this.cardDesign.value = this.cfg.cardRequired ? '' : String(this.cfg.card ? this.cfg.card.variantId : '');
      this.card.variantId = this.cardDesign.value || null;
    } else {
      this.card.variantId = this.cfg.card ? this.cfg.card.variantId : null;
    }
    this.syncAllCards();
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* private mode */ }
  };

  /* ---------- persistence ---------- */

  ByobBuilder.prototype.persist = function () {
    try {
      // localStorage (not session): gift baskets get built over days.
      // savedAt lets restore() expire week-old drafts.
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        savedAt: Date.now(),
        items: Array.from(this.items.values()),
        card: this.card
      }));
    } catch (e) { /* private mode */ }
  };

  ByobBuilder.prototype.restore = function () {
    var saved;
    try {
      saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      return;
    }
    if (!saved) return;
    if (saved.savedAt && Date.now() - saved.savedAt > 7 * 24 * 60 * 60 * 1000) {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
      return;
    }

    var self = this;
    (saved.items || []).forEach(function (line) {
      // Only restore lines whose product card still exists on the page
      var card = self.root.querySelector('[data-byob-card][data-id="' + line.productId + '"]');
      if (card) self.items.set(line.variantId, line);
    });
    if (saved.card && this.cfg.card) {
      this.card.enabled = this.cfg.cardRequired ? true : !!saved.card.enabled;
      this.card.message = saved.card.message || '';
      this.card.from = saved.card.from || '';
      if (this.cardFrom) this.cardFrom.value = this.card.from;
      if (saved.card.variantId) this.card.variantId = saved.card.variantId;
      if (this.cardToggle) this.cardToggle.checked = this.card.enabled;
      if (this.cardFields) this.cardFields.hidden = !this.card.enabled;
      if (this.cardMessage) this.cardMessage.value = this.card.message;
      if (this.msgCount) this.msgCount.textContent = String(this.card.message.length);
      if (this.cardDesign) {
        if (saved.card.variantId) this.cardDesign.value = saved.card.variantId;
        // Re-sync from the select: a stale saved variant leaves it on the
        // placeholder, and the gate must reflect what the shopper sees
        this.card.variantId = this.cardDesign.value || null;
      }
    }
    this.syncAllCards();
  };

  /* ---------- analytics ---------- */

  ByobBuilder.prototype.track = function (event, data) {
    try {
      var payload = { event: event };
      for (var k in data) payload[k] = data[k];
      // Primary path: Shopify web pixels. The store's custom pixel
      // (Settings > Customer events) subscribes to the byob_* events and
      // forwards them to GA4 - there is no tag manager on this store.
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, payload);
      }
      // Secondary: keep the dataLayer push so a tag manager, if one is
      // ever installed, picks the events up with zero theme changes.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) { /* analytics must never break the builder */ }
  };

  /* ---------- boot ---------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
